import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Users, Map, Clock, Download, Trash2, Share2, ArrowRight, X, Edit2, Target,
  AlertTriangle, Heart, Brain, Truck, Users2, Check, RotateCcw, ZoomIn, ZoomOut, Maximize2
} from 'lucide-react'
import { toast } from 'sonner'
import html2canvas from 'html2canvas'
import { format } from 'date-fns'

import {
  AttentionNode, Relation, ProvenanceEvent, CareContext, ContextState,
  Participant, NodeType, RelationKind, Burden, ConsentRecord, EVENT_TAXONOMY, LiveSession, PublicPatternSnapshot
} from './lib/types'
import {
  CREATOR_LINKEDIN, CREATOR_COFFEE, STORAGE, generateId, nowIso, formatDate, timeAgo,
  load, save, getParticipantColor, createEvent, averageBurden, computeClarityScore,
  getNodeTypeLabel, getNodeTypeColor, shortLabel, makePublicSnapshot
} from './lib/utils'

// =====================================================
// ATTENTIO
// Moral attention ecology + decision provenance for patients navigating care.
// Context: collective forgetting after meetings and care movements.
// Method: structured moral attention (affect, responsibility, competence, responsiveness)
// Artifacts: live Attention Ecology Map + immutable Decision Provenance Trail.
// Captures explicitly: task decomposition, perceived burden (4 axes), abandonment point, recovery clarity.
// Disagreement is turned into "productive tension" — surfaced, held, used to prompt joint attention.
// Never a summarizer. The map and trail *are* the memory.
// Built for Cloudflare free tier (Pages + Worker + D1 + Durable Objects + R2 + Turnstile).
// =====================================================

const NODE_TYPES: { type: NodeType; label: string; icon: React.ReactNode }[] = [
  { type: 'perspective', label: 'Perspective', icon: <Users2 size={15} /> },
  { type: 'task', label: 'Care Task', icon: <Target size={15} /> },
  { type: 'need', label: 'Need / Vulnerability', icon: <Heart size={15} /> },
  { type: 'prediction', label: 'Prediction', icon: <Brain size={15} /> },
  { type: 'revision', label: 'Revision / Update', icon: <RotateCcw size={15} /> },
  { type: 'tension', label: 'Productive Tension', icon: <AlertTriangle size={15} /> },
]

const RELATION_KINDS: { kind: RelationKind; label: string }[] = [
  { kind: 'attends_to', label: 'Attends to' },
  { kind: 'predicts', label: 'Predicts outcome of' },
  { kind: 'burdens', label: 'Creates burden on' },
  { kind: 'requires', label: 'Requires attention from' },
  { kind: 'tensions_with', label: 'In productive tension with' },
  { kind: 'revises', label: 'Revises / qualifies' },
]

const ABANDON_STAGES = ['planning', 'communication', 'execution', 'follow-up', 'unknown'] as const

// =====================================================
// LOCAL-FIRST PERSISTENCE (primary experience — works offline beautifully)
// =====================================================

function loadContexts(): CareContext[] {
  return load<CareContext[]>(STORAGE.CONTEXTS, [])
}
function saveContexts(ctxs: CareContext[]) { save(STORAGE.CONTEXTS, ctxs) }

function loadState(id: string): ContextState | null {
  return load<ContextState | null>(STORAGE.STATE_PREFIX + id, null)
}
function saveState(id: string, state: ContextState) { save(STORAGE.STATE_PREFIX + id, state) }

function getWorkerUrl(): string | null { return load<string | null>(STORAGE.CONNECTED_WORKER, null) }
function setWorkerUrl(u: string | null) { if (u) save(STORAGE.CONNECTED_WORKER, u); else localStorage.removeItem(STORAGE.CONNECTED_WORKER) }

// =====================================================
// CONSENT — explicit, granular, revocable, everywhere
// =====================================================

function ConsentGate({
  scope,
  onGrant,
  onCancel,
  children,
}: {
  scope: ConsentRecord['scope']
  onGrant: (note?: string) => void
  onCancel: () => void
  children: React.ReactNode
}) {
  const [note, setNote] = useState('')
  const [checked, setChecked] = useState(false)

  const scopeText = scope === 'personal_trail'
    ? 'Store this record in my personal attention trail for this care context. I can view, revise, or delete anything at any time.'
    : scope === 'room_shared'
    ? 'Share this contribution with the people currently in this live attention room. They will see my nickname and role.'
    : 'Allow this completely stripped, non-narrative pattern (node counts, average burdens, abandonment stages, tension count) to contribute to aggregate public learning about care navigation. No text I wrote, no names, no stories will ever leave this device in identifiable form.'

  return (
    <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="stone w-full max-w-lg rounded-3xl p-6 md:p-8" onClick={e => e.stopPropagation()}>
        <div className="uppercase tracking-[2px] text-[10px] text-ink-dim mb-2">EXPLICIT CONSENT REQUIRED</div>
        <div className="text-2xl tracking-tight leading-none mb-4">Before this is recorded</div>

        <div className="text-[15px] leading-snug text-ink-dim mb-5 pr-1">{children}</div>

        <div className="consent-box p-4 text-sm mb-5">
          <div className="font-medium mb-1.5">What will be stored</div>
          <div className="text-ink-dim text-[13px] leading-snug">{scopeText}</div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer mb-5">
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            className="mt-1 accent-[#a38a5e]"
          />
          <div className="text-[14px] leading-tight">
            I understand and give my consent for the above. I know I can revoke this later from the trail.
          </div>
        </label>

        <div>
          <div className="section-title mb-1.5">Optional note to self (private)</div>
          <input
            className="attention-input w-full text-sm"
            placeholder="e.g. This was after the chemo teaching visit"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onCancel} className="flex-1 py-3 rounded-2xl border border-[#333] active:bg-[#1a1d24]">CANCEL</button>
          <button
            onClick={() => { if (checked) onGrant(note.trim() || undefined) }}
            disabled={!checked}
            className="flex-1 py-3 rounded-2xl bg-white text-black font-medium disabled:bg-[#2a2f38] disabled:text-[#6b7280] active:scale-[0.985]"
          >
            RECORD WITH CONSENT
          </button>
        </div>
        <div className="text-center text-[10px] text-ink-dim mt-4">Nothing is ever sent anywhere without this step.</div>
      </div>
    </div>
  )
}

// =====================================================
// THE ATTENTION ECOLOGY MAP — custom, draggable, beautiful
// =====================================================

function EcologyMap({
  nodes,
  relations,
  participants,
  selectedId,
  onSelect,
  onMove,
  onAddNode,
  onAddRelation,
  onMarkTension,
  viewTransform,
  setViewTransform,
  connectMode,
  setConnectMode,
}: {
  nodes: AttentionNode[]
  relations: Relation[]
  participants: Participant[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  onMove: (id: string, x: number, y: number) => void
  onAddNode: (type: NodeType, x: number, y: number) => void
  onAddRelation: (from: string, to: string, kind: RelationKind) => void
  onMarkTension: (a: string, b: string) => void
  viewTransform: { x: number; y: number; scale: number }
  setViewTransform: (t: { x: number; y: number; scale: number }) => void
  connectMode: boolean
  setConnectMode: (v: boolean) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pendingFrom, setPendingFrom] = useState<string | null>(null)
  const [isPanning, setIsPanning] = useState(false)
  const panStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 })

  const getParticipant = (pid: string) => participants.find(p => p.id === pid)

  // Convert screen coords to map coords
  const screenToMap = (clientX: number, clientY: number) => {
    const rect = containerRef.current!.getBoundingClientRect()
    const mx = (clientX - rect.left - viewTransform.x) / viewTransform.scale
    const my = (clientY - rect.top - viewTransform.y) / viewTransform.scale
    return { x: Math.round(mx), y: Math.round(my) }
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (connectMode) return
    const { x, y } = screenToMap(e.clientX, e.clientY)
    // Default to 'need' on double click — most common moral starting point
    onAddNode('need', x, y)
  }

  const handleNodePointerDown = (e: React.PointerEvent, node: AttentionNode) => {
    e.stopPropagation()
    if (connectMode) {
      if (!pendingFrom) {
        setPendingFrom(node.id)
      } else if (pendingFrom !== node.id) {
        onAddRelation(pendingFrom, node.id, 'attends_to')
        setPendingFrom(null)
        setConnectMode(false)
      }
      return
    }
    onSelect(node.id)

    const startX = e.clientX
    const startY = e.clientY
    const origX = node.position.x
    const origY = node.position.y

    const onMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / viewTransform.scale
      const dy = (ev.clientY - startY) / viewTransform.scale
      onMove(node.id, Math.round(origX + dx), Math.round(origY + dy))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // Background pan
  const startPan = (e: React.PointerEvent) => {
    if (connectMode) return
    setIsPanning(true)
    panStart.current = { x: e.clientX, y: e.clientY, tx: viewTransform.x, ty: viewTransform.y }
  }
  const doPan = (e: React.PointerEvent) => {
    if (!isPanning) return
    const dx = e.clientX - panStart.current.x
    const dy = e.clientY - panStart.current.y
    setViewTransform({
      ...viewTransform,
      x: panStart.current.tx + dx,
      y: panStart.current.ty + dy,
    })
  }
  const endPan = () => setIsPanning(false)

  // Zoom
  const zoom = (delta: number) => {
    const next = Math.max(0.45, Math.min(2.2, viewTransform.scale * delta))
    setViewTransform({ ...viewTransform, scale: next })
  }
  const resetView = () => setViewTransform({ x: 120, y: 80, scale: 1 })

  // Wheel zoom
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const factor = e.deltaY < 0 ? 1.12 : 0.88
    zoom(factor)
  }

  // SVG edges
  const edges = relations.map(rel => {
    const a = nodes.find(n => n.id === rel.fromId)
    const b = nodes.find(n => n.id === rel.toId)
    if (!a || !b) return null
    const x1 = a.position.x
    const y1 = a.position.y
    const x2 = b.position.x
    const y2 = b.position.y
    const isTension = rel.kind === 'tensions_with'
    return (
      <g key={rel.id}>
        <line
          x1={x1} y1={y1} x2={x2} y2={y2}
          className={`edge ${isTension ? 'tension' : ''}`}
          stroke={isTension ? '#b35c5c' : '#3a3f48'}
        />
        {isTension && (
          <circle cx={(x1+x2)/2} cy={(y1+y2)/2} r="3.5" fill="#b35c5c" opacity="0.6" />
        )}
      </g>
    )
  })

  return (
    <div
      ref={containerRef}
      className="map-container map-field w-full h-[520px] md:h-[580px] cursor-grab active:cursor-grabbing select-none"
      onPointerDown={startPan}
      onPointerMove={doPan}
      onPointerUp={endPan}
      onPointerLeave={endPan}
      onDoubleClick={handleDoubleClick}
      onWheel={onWheel}
    >
      <div
        className="absolute inset-0 origin-top-left"
        style={{
          transform: `translate(${viewTransform.x}px, ${viewTransform.y}px) scale(${viewTransform.scale})`,
        }}
      >
        {/* Subtle field lines */}
        <svg width="2400" height="1600" className="absolute pointer-events-none opacity-[0.22]">
          <defs>
            <pattern id="grid" width="38" height="38" patternUnits="userSpaceOnUse">
              <path d="M 38 0 L 0 0 0 38" fill="none" stroke="#2a2f38" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Relations */}
        <svg className="absolute top-0 left-0 pointer-events-none" width="2400" height="1600">
          {edges}
        </svg>

        {/* Nodes */}
        {nodes.map(node => {
          const p = getParticipant(node.createdBy)
          const isSel = node.id === selectedId
          const isTensionType = node.type === 'tension'
          return (
            <motion.div
              key={node.id}
              className={`node ${isSel ? 'ring-1 ring-offset-4 ring-offset-[#0a0b0f] ring-[#a38a5e]' : ''}`}
              style={{ left: node.position.x, top: node.position.y }}
              onPointerDown={(e) => handleNodePointerDown(e, node)}
              whileTap={{ scale: 0.985 }}
            >
              <div className={`node-card ${isTensionType ? 'tension' : ''}`}>
                <div className="node-type" style={{ background: getNodeTypeColor(node.type) + '22', color: getNodeTypeColor(node.type) }}>
                  {getNodeTypeLabel(node.type)}
                </div>
                <div className="font-medium leading-tight tracking-[-0.1px] pr-1">
                  {shortLabel(node.label, 58)}
                </div>
                {node.burden && (
                  <div className="mt-2 flex gap-1">
                    {(['cognitive','emotional','logistical','relational'] as const).map(k => (
                      <div key={k} className="flex-1 h-1 bg-[#22262d] rounded overflow-hidden">
                        <div className="burden-fill" style={{ width: `${(node.burden![k] / 10) * 100}%` }} />
                      </div>
                    ))}
                  </div>
                )}
                {p && (
                  <div className="mt-2 text-[10px] flex items-center gap-1.5 text-ink-dim">
                    <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                    {p.nickname}
                  </div>
                )}
                {node.abandonment && (
                  <div className="mt-1.5 text-[10px] text-[#b35c5c]">abandoned at {node.abandonment.stage}</div>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Overlay controls */}
      <div className="zoom-controls">
        <button className="zoom-btn" onClick={() => zoom(1.18)} title="Zoom in"><ZoomIn size={15} /></button>
        <button className="zoom-btn" onClick={() => zoom(0.82)} title="Zoom out"><ZoomOut size={15} /></button>
        <button className="zoom-btn" onClick={resetView} title="Reset view"><Maximize2 size={15} /></button>
      </div>

      <div className="absolute top-4 left-4 z-30 flex gap-2">
        <button
          onClick={() => { setConnectMode(!connectMode); setPendingFrom(null) }}
          className={`px-3 py-1.5 rounded-2xl text-xs border flex items-center gap-1.5 active:scale-[0.985] ${connectMode ? 'bg-[#a38a5e] text-black border-[#a38a5e]' : 'border-[#333] hover:bg-[#1a1d24]'}`}
        >
          <Share2 size={14} /> {connectMode ? (pendingFrom ? 'Click target node' : 'CONNECT MODE — click two nodes') : 'Connect nodes'}
        </button>
        {connectMode && (
          <button onClick={() => { setConnectMode(false); setPendingFrom(null) }} className="px-3 py-1.5 rounded-2xl text-xs border border-[#333]">CANCEL</button>
        )}
      </div>

      <div className="absolute bottom-4 left-4 z-30 text-[10px] text-ink-dim bg-[#0a0b0f]/80 px-3 py-1 rounded-full border border-[#22262d]">
        Double-click empty space to add a need • Drag nodes • Use toolbar for other types
      </div>
    </div>
  )
}

// =====================================================
// NODE INSPECTOR — moral attention + the 4 required captures
// =====================================================

function NodeInspector({
  node,
  participants,
  onUpdate,
  onDelete,
  onMarkTension,
  onLogAbandon,
  onSetRecovery,
  onAddBurden,
  onRequestConsent,
}: {
  node: AttentionNode
  participants: Participant[]
  onUpdate: (patch: Partial<AttentionNode>) => void
  onDelete: () => void
  onMarkTension: () => void
  onLogAbandon: (stage: any, reason: string, feltBy: string[]) => void
  onSetRecovery: (needed: string, clarity: 'low'|'medium'|'high', by: string[]) => void
  onAddBurden: (b: Burden) => void
  onRequestConsent: (fn: () => void) => void
}) {
  const [burdenDraft, setBurdenDraft] = useState<Burden>(node.burden || { cognitive: 4, emotional: 5, logistical: 3, relational: 4 })
  const [abandonStage, setAbandonStage] = useState<any>('execution')
  const [abandonReason, setAbandonReason] = useState('')
  const [recoveryText, setRecoveryText] = useState(node.recovery?.neededAttention || '')
  const [recoveryClarity, setRecoveryClarity] = useState<'low'|'medium'|'high'>(node.recovery?.clarity || 'medium')

  const p = participants.find(pp => pp.id === node.createdBy)

  const saveBurden = () => {
    onRequestConsent(() => onAddBurden(burdenDraft))
  }

  const saveAbandon = () => {
    if (!abandonReason.trim()) return
    onRequestConsent(() => onLogAbandon(abandonStage, abandonReason.trim(), [node.createdBy]))
  }

  const saveRecovery = () => {
    if (!recoveryText.trim()) return
    onRequestConsent(() => onSetRecovery(recoveryText.trim(), recoveryClarity, [node.createdBy]))
  }

  return (
    <div className="stone p-5 md:p-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[1.5px] text-ink-dim">{getNodeTypeLabel(node.type)} • {p?.nickname}</div>
          <div className="text-[17px] leading-tight font-medium tracking-[-0.2px] mt-1 pr-6">{node.label}</div>
        </div>
        <button onClick={onDelete} className="text-[#b35c5c] p-1"><Trash2 size={17} /></button>
      </div>

      {/* Description / raw attention note */}
      <div>
        <div className="section-title">What is being attended to? (your words)</div>
        <textarea
          className="attention-input w-full min-h-[72px] text-[14px]"
          value={node.description || ''}
          onChange={e => onUpdate({ description: e.target.value })}
          placeholder="The specific reality, worry, or commitment as it actually feels..."
        />
      </div>

      {/* MORAL ATTENTION — the structuring lens */}
      <div className="border border-[#22262d] rounded-2xl p-4">
        <div className="uppercase tracking-[1.5px] text-[10px] text-[#a38a5e] mb-3">MORAL ATTENTION</div>
        <div className="space-y-4 text-sm">
          {([
            ['whoIsAffected', 'Who is most affected if this is ignored or mishandled?'],
            ['responsibility', 'What responsibility does noticing this create for someone?'],
            ['competentResponse', 'What would a capable, humane response actually look like here?'],
            ['feedbackLoop', 'How will we know later whether the attention was adequate?'],
          ] as const).map(([key, label]) => (
            <div key={key}>
              <div className="text-ink-dim text-[12px] mb-1">{label}</div>
              <input
                className="attention-input w-full text-sm py-2"
                value={(node.moral as any)[key] || ''}
                onChange={e => onUpdate({ moral: { ...node.moral, [key]: e.target.value } })}
                placeholder="Say it plainly…"
              />
            </div>
          ))}
        </div>
      </div>

      {/* BURDEN — 4 axes, explicit */}
      <div>
        <div className="section-title flex items-center gap-2 mb-2">
          <Truck size={14} /> PERCEIVED BURDEN (whose attention is being asked?)
        </div>
        {(['cognitive','emotional','logistical','relational'] as const).map(axis => (
          <div key={axis} className="flex items-center gap-3 mb-3">
            <div className="w-24 text-xs text-ink-dim capitalize">{axis}</div>
            <input
              type="range" min={0} max={10} step={1}
              value={burdenDraft[axis]}
              onChange={e => setBurdenDraft({ ...burdenDraft, [axis]: parseInt(e.target.value) })}
              className="flex-1 accent-[#a38a5e]"
            />
            <div className="tabular-nums w-6 text-right text-sm">{burdenDraft[axis]}</div>
          </div>
        ))}
        <button onClick={saveBurden} className="mt-1 text-xs px-4 py-1.5 rounded-2xl border border-[#333] active:bg-[#1a1d24]">RECORD BURDEN WITH CONSENT</button>
      </div>

      {/* TASK DECOMPOSITION hint + ABANDONMENT + RECOVERY */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Abandonment point */}
        <div className="border border-[#22262d] rounded-2xl p-4">
          <div className="uppercase text-[10px] tracking-widest text-[#b35c5c] mb-2">ABANDONMENT POINT</div>
          <select value={abandonStage} onChange={e => setAbandonStage(e.target.value)} className="attention-input w-full text-sm py-2 mb-2">
            {ABANDON_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <textarea
            className="attention-input w-full text-sm min-h-[58px]"
            placeholder="What stopped the attention here? (no shame — just the fact)"
            value={abandonReason}
            onChange={e => setAbandonReason(e.target.value)}
          />
          <button onClick={saveAbandon} disabled={!abandonReason.trim()} className="mt-2 w-full py-2 text-sm rounded-2xl border border-[#b35c5c] text-[#b35c5c] disabled:opacity-40">LOG ABANDONMENT WITH CONSENT</button>
        </div>

        {/* Recovery clarity */}
        <div className="border border-[#22262d] rounded-2xl p-4">
          <div className="uppercase text-[10px] tracking-widest text-[#5c8a6b] mb-2">RECOVERY CLARITY</div>
          <input
            className="attention-input w-full text-sm py-2 mb-2"
            placeholder="What specific attention would make recovery possible?"
            value={recoveryText}
            onChange={e => setRecoveryText(e.target.value)}
          />
          <div className="flex gap-2 mb-2">
            {(['low','medium','high'] as const).map(c => (
              <button key={c} onClick={() => setRecoveryClarity(c)} className={`flex-1 py-1 text-xs rounded-xl border ${recoveryClarity === c ? 'border-[#5c8a6b] bg-[#1a1d24]' : 'border-[#333]'}`}>{c}</button>
            ))}
          </div>
          <button onClick={saveRecovery} disabled={!recoveryText.trim()} className="w-full py-2 text-sm rounded-2xl border border-[#5c8a6b] text-[#5c8a6b] disabled:opacity-40">SET RECOVERY PATH WITH CONSENT</button>
        </div>
      </div>

      {/* Make disagreement useful */}
      <div>
        <button
          onClick={() => onRequestConsent(onMarkTension)}
          className="w-full py-3 rounded-2xl border border-[#b35c5c] text-[#b35c5c] text-sm flex items-center justify-center gap-2 active:bg-[#1a1414]"
        >
          <AlertTriangle size={15} /> SURFACE AS PRODUCTIVE TENSION — this differs from how I see it
        </button>
        <div className="text-[10px] text-ink-dim mt-1.5 text-center">Differences become signals for joint attention, not battles.</div>
      </div>

      <button onClick={onDelete} className="text-xs text-ink-dim hover:text-[#b35c5c] w-full pt-1">Remove this focus from the map</button>
    </div>
  )
}

// =====================================================
// PROVENANCE TRAIL — the decision memory
// =====================================================

function ProvenanceTrail({ events, participants, nodes, onHighlight }: {
  events: ProvenanceEvent[]
  participants: Participant[]
  nodes: AttentionNode[]
  onHighlight: (nodeIds: string[]) => void
}) {
  const sorted = [...events].sort((a, b) => b.ts.localeCompare(a.ts))

  return (
    <div className="provenance-trail space-y-3 pr-1">
      {sorted.length === 0 && <div className="text-ink-dim text-sm">No events yet. Every consented action will appear here.</div>}
      {sorted.map(ev => {
        const p = participants.find(pp => pp.id === ev.participantId)
        const isTension = ev.type === 'tension_marked'
        const isRecovery = ev.type === 'recovery_clarity_set' || ev.type === 'abandonment_logged'
        let relatedNode: AttentionNode | undefined
        if (ev.payload.nodeId) relatedNode = nodes.find(n => n.id === ev.payload.nodeId)

        return (
          <div
            key={ev.id}
            className={`event-card stone-2 p-3.5 text-sm cursor-pointer ${isTension ? 'tension' : ''} ${isRecovery ? 'recovery' : ''}`}
            onClick={() => relatedNode && onHighlight([relatedNode.id])}
          >
            <div className="flex justify-between text-[10px] text-ink-dim">
              <span>{p?.nickname || 'unknown'} • {format(new Date(ev.ts), 'HH:mm')}</span>
              <span>{formatDate(ev.ts)}</span>
            </div>
            <div className="mt-1 font-medium">{EVENT_TAXONOMY[ev.type] || ev.type}</div>
            {ev.payload.label && <div className="text-ink-dim text-xs mt-0.5">{shortLabel(ev.payload.label, 72)}</div>}
            {ev.payload.abandonment && <div className="text-[#b35c5c] text-xs mt-0.5">abandoned at {ev.payload.abandonment.stage}</div>}
            {ev.payload.recoveryClarity && <div className="text-[#5c8a6b] text-xs mt-0.5">recovery clarity: {ev.payload.recoveryClarity}</div>}
          </div>
        )
      })}
    </div>
  )
}

// =====================================================
// INSIGHTS — computed, transparent, no magic
// =====================================================

function Insights({ nodes, relations, participants }: { nodes: AttentionNode[]; relations: Relation[]; participants: Participant[] }) {
  const tensions = relations.filter(r => r.kind === 'tensions_with').length
  const withRecovery = nodes.filter(n => n.recovery).length
  const abandoned = nodes.filter(n => n.abandonment).length
  const clarity = computeClarityScore(nodes, tensions)

  const avgB = averageBurden(nodes)

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="stone p-4">
        <div className="text-[10px] text-ink-dim">ATTENTION DISTRIBUTION</div>
        <div className="mt-3 text-4xl tabular-nums tracking-tighter">{nodes.length}</div>
        <div className="text-xs text-ink-dim -mt-1">attention foci placed</div>
        <div className="mt-4 text-xs space-y-1">
          {(['perspective','task','need','prediction','revision','tension'] as NodeType[]).map(t => {
            const c = nodes.filter(n => n.type === t).length
            return c > 0 ? <div key={t} className="flex justify-between"><span>{getNodeTypeLabel(t)}</span><span className="tabular-nums">{c}</span></div> : null
          })}
        </div>
      </div>

      <div className="stone p-4">
        <div className="text-[10px] text-ink-dim">BURDEN + RECOVERY</div>
        <div className="mt-2 text-sm">Average felt burden (where reported)</div>
        <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          {avgB.cognitive != null && <div>Cognitive <span className="tabular-nums font-medium">{avgB.cognitive}</span></div>}
          {avgB.emotional != null && <div>Emotional <span className="tabular-nums font-medium">{avgB.emotional}</span></div>}
          {avgB.logistical != null && <div>Logistical <span className="tabular-nums font-medium">{avgB.logistical}</span></div>}
          {avgB.relational != null && <div>Relational <span className="tabular-nums font-medium">{avgB.relational}</span></div>}
        </div>
        <div className="mt-4 text-xs text-[#5c8a6b]">Recovery paths named: <span className="font-medium text-ink">{withRecovery}</span> / {abandoned} abandoned</div>
      </div>

      <div className="stone p-4 flex flex-col">
        <div className="text-[10px] text-ink-dim">USEFUL DISAGREEMENT (HELD WITH CARE)</div>
        <div className="mt-auto">
          <div className="text-5xl tabular-nums tracking-[-1.5px]">{tensions}</div>
          <div className="text-sm -mt-1">productive tensions surfaced</div>
          <div className="mt-3 text-xs leading-snug text-ink-dim">
            These are not votes or conflicts. They are places where different people are carrying different realities. The map makes them visible so joint attention can be paid.
          </div>
          <div className="mt-3 text-xs">Attention clarity score: <span className="font-medium tabular-nums">{clarity}</span></div>
        </div>
      </div>
    </div>
  )
}

// =====================================================
// MAIN APP
// =====================================================

export default function Attentio() {
  // --- Global ---
  const [phase, setPhase] = useState<'landing' | 'dashboard' | 'workspace'>('landing')
  const [contexts, setContexts] = useState<CareContext[]>(() => loadContexts())
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [workerUrl, setWorkerUrlState] = useState<string | null>(() => getWorkerUrl())

  // Current workspace state
  const [state, setState] = useState<ContextState | null>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [activeView, setActiveView] = useState<'map' | 'trail' | 'insights'>('map')
  const [connectMode, setConnectMode] = useState(false)
  const [viewTransform, setViewTransform] = useState({ x: 140, y: 70, scale: 1 })

  // Live session (demo simulator + real prep)
  const [liveSession, setLiveSession] = useState<LiveSession | null>(null)
  const [simulatedParticipants, setSimulatedParticipants] = useState<Participant[]>([])

  // Consent gate
  const [pendingConsentAction, setPendingConsentAction] = useState<(() => void) | null>(null)
  const [consentScope, setConsentScope] = useState<ConsentRecord['scope']>('personal_trail')

  // Quick capture
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false)

  const currentContext = useMemo(() => contexts.find(c => c.id === currentId), [contexts, currentId])
  const workerConnected = !!workerUrl

  // Load state when currentId changes
  useEffect(() => {
    if (!currentId) { setState(null); return }
    const loaded = loadState(currentId)
    if (loaded) {
      setState(loaded)
    } else {
      // bootstrap empty state
      const fresh: ContextState = {
        context: contexts.find(c => c.id === currentId)!,
        participants: [],
        nodes: [],
        relations: [],
        events: [],
        consents: [],
      }
      setState(fresh)
      saveState(currentId, fresh)
    }
    setSelectedNodeId(null)
    setViewTransform({ x: 140, y: 70, scale: 1 })
  }, [currentId])

  // Persist state whenever it changes
  useEffect(() => {
    if (state && currentId) {
      saveState(currentId, state)
      // touch updatedAt on context list
      setContexts(prev => prev.map(c => c.id === currentId ? { ...c, updatedAt: nowIso() } : c))
    }
  }, [state])

  // Persist contexts list
  useEffect(() => { saveContexts(contexts) }, [contexts])

  // Derived
  const nodes = state?.nodes ?? []
  const relations = state?.relations ?? []
  const events = state?.events ?? []
  const participants = useMemo(() => {
    const base = state?.participants ?? []
    return [...base, ...simulatedParticipants]
  }, [state?.participants, simulatedParticipants])

  const selectedNode = nodes.find(n => n.id === selectedNodeId) || null
  const tensionsCount = relations.filter(r => r.kind === 'tensions_with').length

  // =====================================================
  // CORE MUTATORS (all go through consent gate for the important ones)
  // =====================================================

  function requestConsent(scope: ConsentRecord['scope'], action: () => void) {
    setConsentScope(scope)
    setPendingConsentAction(() => action)
  }

  function grantConsent(note?: string) {
    if (!pendingConsentAction || !state || !currentId) return

    const consent: ConsentRecord = {
      id: generateId(8),
      scope: consentScope,
      granted: true,
      grantedAt: nowIso(),
      note,
    }

    // Always log the consent grant
    const consentEvent = createEvent('consent_granted', state.participants[0]?.id || 'self', { scope: consentScope })

    pendingConsentAction()

    setState(prev => prev ? {
      ...prev,
      consents: [...prev.consents, consent],
      events: [...prev.events, consentEvent],
    } : prev)

    setPendingConsentAction(null)
    toast.success('Recorded with your consent.')
  }

  function cancelConsent() {
    setPendingConsentAction(null)
    toast('Nothing recorded.')
  }

  // Create new context (with initial participant + consent)
  function createContext(title: string, description: string, firstParticipant: { nickname: string; role: Participant['role'] }) {
    const id = generateId(9)
    const now = nowIso()

    const p: Participant = {
      id: generateId(8),
      nickname: firstParticipant.nickname || 'You',
      role: firstParticipant.role,
      joinedAt: now,
      color: getParticipantColor(0),
    }

    const ctx: CareContext = {
      id,
      title: title.trim() || 'Unnamed care context',
      description: description.trim() || undefined,
      createdAt: now,
      updatedAt: now,
    }

    const initialEvent = createEvent('context_created', p.id, { title: ctx.title })

    const initialState: ContextState = {
      context: ctx,
      participants: [p],
      nodes: [],
      relations: [],
      events: [initialEvent],
      consents: [],
    }

    setContexts(prev => [ctx, ...prev])
    saveState(id, initialState)
    setCurrentId(id)
    setState(initialState)
    setPhase('workspace')
    setActiveView('map')
    toast.success('New attention ecology created. Everything starts here.')
  }

  function openContext(id: string) {
    setCurrentId(id)
    setPhase('workspace')
    setLiveSession(null)
    setSimulatedParticipants([])
  }

  function deleteContext(id: string) {
    if (!confirm('Delete this entire attention map and trail from this device? This cannot be undone.')) return
    setContexts(prev => prev.filter(c => c.id !== id))
    localStorage.removeItem(STORAGE.STATE_PREFIX + id)
    if (currentId === id) {
      setCurrentId(null)
      setState(null)
      setPhase('dashboard')
    }
    toast('Context deleted from this device.')
  }

  // Add node (with consent for anything meaningful)
  function addNode(type: NodeType, x: number, y: number, label?: string) {
    if (!state) return
    const me = state.participants[0] || { id: 'self', nickname: 'You' } as any

    const node: AttentionNode = {
      id: generateId(9),
      type,
      label: label || (type === 'need' ? 'What needs attention here?' : 'New focus'),
      position: { x, y },
      createdBy: me.id,
      createdAt: nowIso(),
      moral: {},
    }

    const ev = createEvent('node_added', me.id, { nodeId: node.id, type, label: node.label })

    setState(prev => prev ? {
      ...prev,
      nodes: [...prev.nodes, node],
      events: [...prev.events, ev],
    } : prev)

    setSelectedNodeId(node.id)
    setActiveView('map')
  }

  function updateNode(id: string, patch: Partial<AttentionNode>) {
    if (!state) return
    setState(prev => prev ? {
      ...prev,
      nodes: prev.nodes.map(n => n.id === id ? { ...n, ...patch } : n),
      events: [...prev.events, createEvent('node_updated', prev.participants[0]?.id || 'self', { nodeId: id, ...Object.keys(patch) })],
    } : prev)
  }

  function deleteNode(id: string) {
    if (!state) return
    setState(prev => prev ? {
      ...prev,
      nodes: prev.nodes.filter(n => n.id !== id),
      relations: prev.relations.filter(r => r.fromId !== id && r.toId !== id),
    } : prev)
    if (selectedNodeId === id) setSelectedNodeId(null)
  }

  function addRelation(fromId: string, toId: string, kind: RelationKind) {
    if (!state) return
    const exists = state.relations.some(r => r.fromId === fromId && r.toId === toId && r.kind === kind)
    if (exists) return

    const rel: Relation = {
      id: generateId(8),
      fromId, toId, kind,
      createdAt: nowIso(),
      createdBy: state.participants[0]?.id || 'self',
    }
    const ev = createEvent(kind === 'tensions_with' ? 'tension_marked' : 'node_updated', rel.createdBy, { from: fromId, to: toId, kind })

    setState(prev => prev ? {
      ...prev,
      relations: [...prev.relations, rel],
      events: [...prev.events, ev],
    } : prev)
  }

  function markTension(a: string, b: string) {
    addRelation(a, b, 'tensions_with')
    // Also auto-offer a joint revision node near the visual center
    const na = state!.nodes.find(n => n.id === a)
    const nb = state!.nodes.find(n => n.id === b)
    if (na && nb) {
      const mx = Math.round((na.position.x + nb.position.x) / 2) + 40
      const my = Math.round((na.position.y + nb.position.y) / 2) - 30
      const joint: AttentionNode = {
        id: generateId(9),
        type: 'revision',
        label: 'What joint attention would honor both realities?',
        position: { x: mx, y: my },
        createdBy: state!.participants[0]?.id || 'self',
        createdAt: nowIso(),
        moral: { whoIsAffected: 'Both perspectives matter here' },
      }
      setState(prev => prev ? {
        ...prev,
        nodes: [...prev.nodes, joint],
        events: [...prev.events, createEvent('revision_proposed', joint.createdBy, { nodeId: joint.id, reason: 'joint attention from tension' })],
      } : prev)
      setSelectedNodeId(joint.id)
      toast('Tension surfaced. A joint attention node was added — edit it to name what would actually help.')
    }
  }

  // The four explicit captures (called only after consent gate)
  function logBurden(nodeId: string, burden: Burden) {
    if (!state) return
    updateNode(nodeId, { burden })
    const ev = createEvent('burden_reported', state.participants[0]?.id || 'self', { nodeId, burden })
    setState(prev => prev ? { ...prev, events: [...prev.events, ev] } : prev)
  }

  function logAbandonment(nodeId: string, stage: any, reason: string, feltBy: string[]) {
    if (!state) return
    updateNode(nodeId, {
      abandonment: { stage, reason, feltBy, at: nowIso() }
    })
    const ev = createEvent('abandonment_logged', state.participants[0]?.id || 'self', { nodeId, abandonment: { stage, reason } })
    setState(prev => prev ? { ...prev, events: [...prev.events, ev] } : prev)
  }

  function setRecovery(nodeId: string, neededAttention: string, clarity: 'low'|'medium'|'high', byWhom: string[]) {
    if (!state) return
    updateNode(nodeId, {
      recovery: { neededAttention, clarity, byWhom, at: nowIso() }
    })
    const ev = createEvent('recovery_clarity_set', state.participants[0]?.id || 'self', { nodeId, recoveryClarity: clarity })
    setState(prev => prev ? { ...prev, events: [...prev.events, ev] } : prev)
  }

  // Quick capture bar action (decomposition / burden / abandonment / recovery in one flow)
  function quickCapture(type: 'task' | 'burden' | 'abandon' | 'recovery', value: string) {
    if (!state) return
    const me = state.participants[0]
    if (!me) return

    let newNode: AttentionNode | null = null

    if (type === 'task') {
      newNode = {
        id: generateId(9), type: 'task', label: value, position: { x: 380 + Math.random()*120, y: 210 + Math.random()*80 },
        createdBy: me.id, createdAt: nowIso(), moral: {},
      }
    } else if (type === 'burden') {
      // find or create a recent task/need and attach burden
      const recent = [...nodes].reverse().find(n => n.type === 'task' || n.type === 'need')
      if (recent) {
        logBurden(recent.id, { cognitive: 6, emotional: 7, logistical: 5, relational: 4 })
        return
      }
      newNode = { id: generateId(9), type: 'need', label: value || 'Unnamed burden', position: { x: 320, y: 260 }, createdBy: me.id, createdAt: nowIso(), moral: {} }
    } else if (type === 'abandon') {
      const recent = [...nodes].reverse().find(n => n.type === 'task')
      if (recent) {
        logAbandonment(recent.id, 'execution', value || 'attention dropped', [me.id])
        return
      }
    } else {
      const recent = [...nodes].reverse().find(n => n.abandonment)
      if (recent) {
        setRecovery(recent.id, value || 'Need renewed attention from...', 'medium', [me.id])
        return
      }
    }

    if (newNode) {
      const ev = createEvent('node_added', me.id, { nodeId: newNode.id, label: newNode.label })
      setState(prev => prev ? {
        ...prev,
        nodes: [...prev.nodes, newNode!],
        events: [...prev.events, ev],
      } : prev)
      setSelectedNodeId(newNode.id)
    }
  }

  // =====================================================
  // LIVE SESSION (simulator today — real DO + WS ready)
  // =====================================================

  function startLiveSession() {
    if (!state) return
    const code = 'ATTN-' + generateId(5).toUpperCase()
    const session: LiveSession = { code, activeParticipants: [state.participants[0]?.id || 'self'], startedAt: nowIso() }
    setLiveSession(session)
    setSimulatedParticipants([])
    const ev = createEvent('live_session_started', state.participants[0]?.id || 'self', { code })
    setState(prev => prev ? { ...prev, events: [...prev.events, ev] } : prev)
    toast.success(`Live attention session started. Code: ${code}. Share with people in the room.`)
  }

  function simulateJoin(nickname: string, role: Participant['role']) {
    if (!liveSession || !state) return
    const p: Participant = {
      id: generateId(8),
      nickname,
      role,
      joinedAt: nowIso(),
      color: getParticipantColor(participants.length),
    }
    setSimulatedParticipants(prev => [...prev, p])
    toast(`${nickname} joined the attention circle (simulated — perfect for testing multi-perspective).`)
  }

  function endLive() {
    setLiveSession(null)
    setSimulatedParticipants([])
  }

  // =====================================================
  // EXPORT + R2 (client beautiful export + optional worker archive)
  // =====================================================

  async function exportProvenance() {
    if (!state || !currentContext) return

    const exportEl = document.getElementById('export-area')
    let imageData: string | null = null

    if (exportEl) {
      const canvas = await html2canvas(exportEl, { backgroundColor: '#0a0b0f', scale: 1.6 })
      imageData = canvas.toDataURL('image/png')
    }

    const bundle = {
      exportedAt: nowIso(),
      context: currentContext,
      participants: participants,
      nodes,
      relations,
      events,
      clarityScore: computeClarityScore(nodes, tensionsCount),
    }

    // Download JSON
    const jsonBlob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
    const jsonUrl = URL.createObjectURL(jsonBlob)
    const a = document.createElement('a')
    a.href = jsonUrl
    a.download = `attentio-${currentContext.id.slice(0,6)}.json`
    a.click()
    URL.revokeObjectURL(jsonUrl)

    // Also download image of current map if possible
    if (imageData) {
      const imgA = document.createElement('a')
      imgA.href = imageData
      imgA.download = `attentio-map-${currentContext.id.slice(0,6)}.png`
      imgA.click()
    }

    // Log + optional archive
    const ev = createEvent('map_exported', state.participants[0]?.id || 'self', { format: 'json+png' })
    setState(prev => prev ? { ...prev, events: [...prev.events, ev] } : prev)

    toast.success('Provenance bundle downloaded. You own this completely.')

    // If worker connected, offer to archive to R2 (the real thing)
    if (workerConnected) {
      // In real deploy this would POST the bundle to /api/archive with consent
      // For now we just surface the intent
      setTimeout(() => toast('Worker connected — in a real deploy this would also be archived to your R2 bucket.'), 900)
    }
  }

  // Public patterns with Turnstile (stripped, opt-in, aggregate only)
  const [showTurnstile, setShowTurnstile] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')

  function contributePublicPatterns() {
    if (!state) return
    const snapshot: PublicPatternSnapshot = makePublicSnapshot(nodes, relations)
    // In real life: render real Turnstile widget here, get token, POST /api/public-pattern with token + snapshot
    setShowTurnstile(true)
    // For this complete MVP we simulate success after "verify"
    setTimeout(() => {
      setTurnstileToken('demo-verified-' + generateId(6))
    }, 420)
  }

  function submitPublicPattern() {
    if (!turnstileToken) return
    // Here we would call the worker to verify Turnstile then store the stripped snapshot
    toast.success('Thank you. Stripped structural pattern contributed to collective learning about care navigation.')
    setShowTurnstile(false)
    setTurnstileToken('')
    // In a full deploy the worker would also write a coarse event to Analytics Engine
  }

  // =====================================================
  // WORKER CONNECT (same pattern as altar — user controls when private becomes collective)
  // =====================================================

  function connectWorker() {
    const url = prompt('Enter your deployed ATTENTIO worker URL (https://attentio....workers.dev)', workerUrl || 'https://')
    if (!url || !url.startsWith('http')) return
    setWorkerUrlState(url)
    setWorkerUrl(url)
    toast.success('Connected. Future exports and public patterns can use the real backend.')
  }
  function disconnectWorker() {
    setWorkerUrlState(null)
    setWorkerUrl(null)
    toast('Disconnected. Everything stays on your device.')
  }

  // =====================================================
  // KEYBOARD
  // =====================================================
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'n' && phase === 'workspace') {
        // quick add need
        addNode('need', 340 + Math.random() * 80, 180 + Math.random() * 60)
      }
      if (e.key.toLowerCase() === 't' && phase === 'workspace') setActiveView('trail')
      if (e.key.toLowerCase() === 'm' && phase === 'workspace') setActiveView('map')
      if (e.key === 'Escape') {
        setSelectedNodeId(null)
        setConnectMode(false)
        setQuickCaptureOpen(false)
      }
      if (e.key === '?' ) {
        setPhase('landing')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase])

  // =====================================================
  // RENDER
  // =====================================================

  const me = state?.participants[0]

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-[#e8e9ed] selection:bg-[#334155]">
      {/* Persistent calm header */}
      <div className="sticky top-0 z-50 border-b border-[#1f2329] bg-[#0a0b0f]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            <div onClick={() => { setPhase('landing'); setCurrentId(null) }} className="font-display text-2xl tracking-[-1.8px] font-semibold cursor-pointer">ATTENTIO</div>
            <div className="text-[9px] px-2 py-px rounded bg-[#1a1d24] text-ink-dim tracking-[1.5px] hidden sm:block">MORAL ATTENTION FOR CARE</div>
          </div>

          <div className="flex items-center gap-1.5 md:gap-3 text-xs">
            {phase !== 'landing' && (
              <button onClick={() => setPhase('dashboard')} className="px-3 py-1.5 hover:bg-[#121317] rounded-2xl flex items-center gap-1.5">
                <Map size={15} /> MY CONTEXTS
              </button>
            )}
            {phase === 'workspace' && currentContext && (
              <>
                <button onClick={() => setActiveView('map')} className={`px-3 py-1.5 rounded-2xl ${activeView === 'map' ? 'bg-[#1a1d24]' : 'hover:bg-[#121317]'}`}>MAP</button>
                <button onClick={() => setActiveView('trail')} className={`px-3 py-1.5 rounded-2xl ${activeView === 'trail' ? 'bg-[#1a1d24]' : 'hover:bg-[#121317]'}`}>TRAIL</button>
                <button onClick={() => setActiveView('insights')} className={`px-3 py-1.5 rounded-2xl ${activeView === 'insights' ? 'bg-[#1a1d24]' : 'hover:bg-[#121317]'}`}>INSIGHTS</button>
              </>
            )}
            {phase === 'workspace' && (
              <button onClick={exportProvenance} className="ml-1 px-4 py-1.5 bg-white text-black rounded-2xl text-xs font-medium flex items-center gap-1.5 active:scale-[0.985]">
                <Download size={14} /> EXPORT PROVENANCE
              </button>
            )}
            <button onClick={() => setPhase('landing')} className="px-3 py-1.5 hover:bg-[#121317] rounded-2xl text-xs">ABOUT</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-8 pb-20">
        {/* LANDING — thesis + why this specific design */}
        {phase === 'landing' && (
          <div className="max-w-3xl">
            <div className="uppercase tracking-[3px] text-xs text-ink-dim mb-3">A COGNITIVE INSTRUMENT FOR CARE</div>
            <h1 className="font-display text-6xl md:text-7xl tracking-[-3.8px] leading-[0.9]">
              After the meeting ends,<br />who remembers what mattered?
            </h1>

            <p className="mt-6 text-xl text-ink-dim max-w-2xl">
              Patients and families carry the real cognitive and moral load of care coordination. 
              After every appointment, tumor board, discharge meeting, or family huddle, attention fragments. 
              Tasks blur. The weight someone felt is never named. Disagreements get smoothed over until they become dropped balls.
            </p>

            <div className="mt-8 grid md:grid-cols-2 gap-3 text-[15px]">
              <div className="stone p-5">
                <div className="font-medium mb-2">What we make visible</div>
                <ul className="space-y-2 text-sm text-ink-dim list-none pl-0">
                  <li>• Task decomposition — what actually needs to happen</li>
                  <li>• Perceived burden on four real axes (cognitive, emotional, logistical, relational)</li>
                  <li>• Abandonment points — exactly where attention died</li>
                  <li>• Recovery clarity — what specific attention would make it possible again</li>
                </ul>
              </div>
              <div className="stone p-5">
                <div className="font-medium mb-2">Moral attention, not neutral notes</div>
                <div className="text-sm text-ink-dim leading-snug">
                  Every node asks: Who is most affected? What responsibility does this noticing create? 
                  What would competent care actually look like? How will we know it was enough?
                  These questions are not therapy. They are the minimum structure needed to stop collective forgetting.
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button onClick={() => setPhase('dashboard')} className="px-8 py-4 bg-white text-black text-lg font-medium rounded-2xl flex items-center gap-3 active:scale-[0.985]">
                BEGIN AN ATTENTION MAP <ArrowRight />
              </button>
            </div>

            <div className="mt-14 text-xs text-ink-dim border-t border-[#22262d] pt-5 space-y-2">
              <div>Disagreement is not a problem to be voted away. When two people are tracking different realities, the map surfaces it as a <span className="text-[#b35c5c]">productive tension</span> and invites a joint attention proposal. No likes, no conflict points, no gamification of pain.</div>
              <div className="pt-2">Everything is local-first and under your control. You decide when (or if) anything ever leaves your device.</div>
            </div>

            {/* Prominent creator credits — per human rules */}
            <div className="mt-10 pt-6 border-t border-[#22262d] text-sm creator-credit flex flex-wrap gap-x-5 gap-y-1">
              <a href={CREATOR_LINKEDIN} target="_blank" rel="noreferrer">LinkedIn — Abhay Pratap Singh</a>
              <a href={CREATOR_COFFEE} target="_blank" rel="noreferrer">Buy me a coffee</a>
              <span className="text-ink-dim">Built as a serious instrument on Cloudflare free tier (Pages, Workers, D1, Durable Objects, R2, Turnstile).</span>
            </div>
          </div>
        )}

        {/* DASHBOARD */}
        {phase === 'dashboard' && (
          <div>
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="ritual-step">YOUR CARE ATTENTION ARCHIVE</div>
                <div className="text-4xl tracking-[-1.2px]">Contexts you have held attention for</div>
              </div>
              <button onClick={() => {
                const title = prompt('Context title (e.g. "Post-discharge oncology planning 12 May")') || ''
                if (!title) return
                const desc = prompt('Short description (meeting, transition, etc.)') || ''
                const nick = prompt('Your nickname in this circle (e.g. "Patient" or your first name)') || 'You'
                const role = (prompt('Your role (patient / caregiver / clinician / advocate / other)') || 'patient') as any
                createContext(title, desc, { nickname: nick, role })
              }} className="px-6 py-3 rounded-2xl bg-white text-black font-medium flex items-center gap-2 active:scale-[0.985]">
                <Plus size={17} /> NEW ATTENTION MAP
              </button>
            </div>

            {contexts.length === 0 && (
              <div className="stone p-8 max-w-md text-[15px]">
                No maps yet. Start one after any care meeting, discharge, or family + provider conversation.
                The first 60 seconds of naming what actually needs attention changes what gets forgotten.
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {contexts.map(ctx => {
                const s = loadState(ctx.id)
                const nodeCount = s?.nodes.length || 0
                const tCount = s?.relations.filter(r => r.kind === 'tensions_with').length || 0
                return (
                  <div key={ctx.id} className="stone p-5 flex flex-col">
                    <div className="text-xs text-ink-dim">{formatDate(ctx.createdAt)} • {nodeCount} foci • {tCount} tensions held</div>
                    <div className="mt-2 text-[17px] leading-tight font-medium tracking-[-0.2px]">{ctx.title}</div>
                    {ctx.description && <div className="mt-1 text-sm text-ink-dim line-clamp-2">{ctx.description}</div>}

                    <div className="mt-auto pt-5 flex gap-2">
                      <button onClick={() => openContext(ctx.id)} className="flex-1 py-2.5 rounded-2xl bg-white text-black text-sm font-medium active:scale-[0.985]">OPEN WORKSPACE</button>
                      <button onClick={() => deleteContext(ctx.id)} className="px-4 py-2.5 rounded-2xl border border-[#333] text-sm text-ink-dim">DELETE</button>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-8 text-xs text-ink-dim">Everything lives on this device until you choose to connect a worker or export. You can always purge.</div>
          </div>
        )}

        {/* THE WORKSPACE — full functional heart */}
        {phase === 'workspace' && state && currentContext && (
          <div>
            {/* Context header */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[2px] text-ink-dim">ATTENTION ECOLOGY FOR</div>
                <div className="text-3xl tracking-[-1px] font-medium">{currentContext.title}</div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {participants.length > 0 && (
                  <div className="flex items-center -space-x-1 pr-2">
                    {participants.slice(0, 5).map((p, i) => (
                      <div key={i} className="w-6 h-6 rounded-full ring-2 ring-[#0a0b0f] flex items-center justify-center text-[9px]" style={{ background: p.color }}>{p.nickname[0]}</div>
                    ))}
                  </div>
                )}
                <button onClick={startLiveSession} className="px-4 py-1.5 rounded-2xl border border-[#333] flex items-center gap-1.5 text-xs active:bg-[#1a1d24]">
                  <Users size={14} /> {liveSession ? 'LIVE SESSION ACTIVE' : 'START LIVE ATTENTION SESSION'}
                </button>
                <button onClick={() => setQuickCaptureOpen(true)} className="px-4 py-1.5 rounded-2xl border border-[#333] flex items-center gap-1.5 text-xs">QUICK CAPTURE</button>
              </div>
            </div>

            {/* Live session bar */}
            <AnimatePresence>
              {liveSession && (
                <div className="mb-4 stone p-3 flex flex-wrap items-center gap-3 text-sm">
                  <div className="font-mono text-xs px-3 py-1 bg-[#1a1d24] rounded">CODE: {liveSession.code}</div>
                  <div className="text-ink-dim">People in this attention circle can add perspectives in real time.</div>
                  <div className="flex-1" />
                  <button onClick={() => simulateJoin('Family member', 'caregiver')} className="text-xs border border-[#333] px-3 py-1 rounded-2xl">+ Simulate family joining</button>
                  <button onClick={() => simulateJoin('Nurse navigator', 'coordinator')} className="text-xs border border-[#333] px-3 py-1 rounded-2xl">+ Simulate coordinator</button>
                  <button onClick={endLive} className="text-xs text-[#b35c5c]">END LIVE SESSION</button>
                </div>
              )}
            </AnimatePresence>

            {/* Toolbar */}
            <div className="flex flex-wrap gap-2 mb-3">
              {NODE_TYPES.map(nt => (
                <button
                  key={nt.type}
                  onClick={() => addNode(nt.type, 260 + Math.random() * 180, 140 + Math.random() * 140)}
                  className="flex items-center gap-2 text-xs px-3.5 py-2 rounded-2xl border border-[#22262d] hover:border-[#3a3f48] active:bg-[#1a1d24]"
                >
                  {nt.icon} {nt.label}
                </button>
              ))}
              <div className="flex-1" />
              <button onClick={contributePublicPatterns} className="text-xs px-4 py-2 rounded-2xl border border-[#333] flex items-center gap-1.5">CONTRIBUTE STRIPPED PATTERNS (PUBLIC LEARNING)</button>
            </div>

            {/* MAIN SURFACE */}
            {activeView === 'map' && (
              <>
                <EcologyMap
                  nodes={nodes}
                  relations={relations}
                  participants={participants}
                  selectedId={selectedNodeId}
                  onSelect={setSelectedNodeId}
                  onMove={(id, x, y) => updateNode(id, { position: { x, y } })}
                  onAddNode={addNode}
                  onAddRelation={addRelation}
                  onMarkTension={(a, b) => markTension(a, b)}
                  viewTransform={viewTransform}
                  setViewTransform={setViewTransform}
                  connectMode={connectMode}
                  setConnectMode={setConnectMode}
                />

                {/* Inspector */}
                <div className="mt-4">
                  {!selectedNode && (
                    <div className="text-sm text-ink-dim">Select any node on the map to add moral attention questions, burden, abandonment point, or recovery clarity. Or double-click empty space to place a new need.</div>
                  )}
                  {selectedNode && (
                    <NodeInspector
                      node={selectedNode}
                      participants={participants}
                      onUpdate={(patch) => updateNode(selectedNode.id, patch)}
                      onDelete={() => deleteNode(selectedNode.id)}
                      onMarkTension={() => {
                        // open a simple picker — for MVP pick the most recent other node
                        const other = nodes.find(n => n.id !== selectedNode.id)
                        if (other) markTension(selectedNode.id, other.id)
                      }}
                      onLogAbandon={(stage, reason, felt) => logAbandonment(selectedNode.id, stage, reason, felt)}
                      onSetRecovery={(needed, clarity, by) => setRecovery(selectedNode.id, needed, clarity, by)}
                      onAddBurden={(b) => logBurden(selectedNode.id, b)}
                      onRequestConsent={(fn) => requestConsent('personal_trail', fn)}
                    />
                  )}
                </div>
              </>
            )}

            {activeView === 'trail' && (
              <div className="stone p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="ritual-step">DECISION PROVENANCE TRAIL</div>
                    <div className="text-xl">Every consented act of attention, in time</div>
                  </div>
                  <button onClick={exportProvenance} className="text-xs px-4 py-2 border border-[#333] rounded-2xl flex items-center gap-1"><Download size={14} /> FULL EXPORT</button>
                </div>
                <ProvenanceTrail events={events} participants={participants} nodes={nodes} onHighlight={(ids) => { setActiveView('map'); setSelectedNodeId(ids[0] || null) }} />
              </div>
            )}

            {activeView === 'insights' && (
              <div>
                <Insights nodes={nodes} relations={relations} participants={participants} />
                <div className="mt-4 text-xs text-ink-dim max-w-prose">
                  These numbers are computed only from what you and the people in this circle have explicitly entered. 
                  No model is guessing at your life. The goal is legibility, not optimization.
                </div>
              </div>
            )}

            {/* Hidden export area for html2canvas (beautiful provenance card) */}
            <div id="export-area" className="hidden">
              <div style={{ width: 820, padding: 42, background: '#0a0b0f', color: '#e8e9ed', fontFamily: 'system-ui' }}>
                <div style={{ fontSize: 11, letterSpacing: 3, opacity: 0.5 }}>ATTENTIO • MORAL ATTENTION ECOLOGY</div>
                <div style={{ fontSize: 26, marginTop: 10, lineHeight: 1.05 }}>{currentContext.title}</div>
                <div style={{ marginTop: 22, fontSize: 13, opacity: 0.75 }}>{nodes.length} attention foci • {tensionsCount} productive tensions held</div>
                <div style={{ marginTop: 32, fontSize: 12, opacity: 0.5 }}>Exported {format(new Date(), 'yyyy-MM-dd')} • Private to you unless you choose otherwise • {CREATOR_LINKEDIN}</div>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER CREDITS — always visible and prominent */}
        <div className="mt-16 pt-8 border-t border-[#22262d] text-xs text-ink-dim creator-credit flex flex-wrap gap-x-5 gap-y-1 items-center">
          <span>Built with care for patients and the people who carry them.</span>
          <a href={CREATOR_LINKEDIN} target="_blank" rel="noreferrer" className="hover:text-white">LinkedIn — Abhay Pratap Singh</a>
          <a href={CREATOR_COFFEE} target="_blank" rel="noreferrer" className="hover:text-white">Buy me a coffee</a>
          <span>Complete local-first app. Real Cloudflare architecture (D1 + DO + R2 + Turnstile) provided in worker + wrangler.toml.</span>
        </div>
      </div>

      {/* QUICK CAPTURE MODAL — the four things, fast, after meetings */}
      <AnimatePresence>
        {quickCaptureOpen && (
          <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" onClick={() => setQuickCaptureOpen(false)}>
            <div className="stone w-full max-w-md rounded-3xl p-6" onClick={e => e.stopPropagation()}>
              <div className="uppercase tracking-[2px] text-xs mb-2 text-ink-dim">60-SECOND POST-MEETING CAPTURE</div>
              <div className="text-xl mb-4">Name one thing before it disappears.</div>

              <div className="space-y-2">
                <button onClick={() => { const v = prompt('Task or commitment that needs to happen?') || ''; if (v) quickCapture('task', v); setQuickCaptureOpen(false) }} className="w-full text-left p-4 rounded-2xl border border-[#22262d] hover:border-[#3a3f48]">Decompose a task or commitment</button>
                <button onClick={() => { const v = prompt('What feels heavy right now?') || ''; if (v) quickCapture('burden', v); setQuickCaptureOpen(false) }} className="w-full text-left p-4 rounded-2xl border border-[#22262d] hover:border-[#3a3f48]">Log perceived burden (4 axes)</button>
                <button onClick={() => { const v = prompt('Where did attention drop? What stage?') || ''; if (v) quickCapture('abandon', v); setQuickCaptureOpen(false) }} className="w-full text-left p-4 rounded-2xl border border-[#22262d] hover:border-[#3a3f48]">Log an abandonment point</button>
                <button onClick={() => { const v = prompt('What specific attention would allow recovery?') || ''; if (v) quickCapture('recovery', v); setQuickCaptureOpen(false) }} className="w-full text-left p-4 rounded-2xl border border-[#22262d] hover:border-[#3a3f48]">Name a recovery path</button>
              </div>
              <button onClick={() => setQuickCaptureOpen(false)} className="mt-4 w-full py-3 text-xs border border-[#333] rounded-2xl">CLOSE</button>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* TURNSTILE / PUBLIC CONTRIBUTION */}
      <AnimatePresence>
        {showTurnstile && (
          <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4" onClick={() => setShowTurnstile(false)}>
            <div className="stone max-w-sm w-full p-6 rounded-3xl" onClick={e => e.stopPropagation()}>
              <div className="text-sm mb-3">Contribute stripped structural patterns only (no text you wrote, no identities). This helps surface common abandonment points and burden shapes across patients — without ever exposing stories.</div>

              <div className="p-4 border border-[#22262d] rounded-2xl mb-4 text-xs text-ink-dim">
                Cloudflare Turnstile would render here in production.<br />For this complete build: click verify to simulate successful private attestation.
              </div>

              <button onClick={submitPublicPattern} disabled={!turnstileToken} className="w-full py-3 rounded-2xl bg-white text-black disabled:bg-[#2a2f38] disabled:text-[#6b7280]">VERIFY + CONTRIBUTE ANONYMIZED PATTERNS</button>
              <button onClick={() => setShowTurnstile(false)} className="mt-2 w-full py-2 text-xs">CANCEL</button>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* CONSENT GATE — used for every meaningful capture */}
      <AnimatePresence>
        {pendingConsentAction && (
          <ConsentGate scope={consentScope} onGrant={grantConsent} onCancel={cancelConsent}>
            This action will create a permanent entry in the provenance trail for this care context.
          </ConsentGate>
        )}
      </AnimatePresence>
    </div>
  )
}
